/** CRM-3 Documents — lista globale + upload */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Upload, FileText, Trash2, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TIPI = ["polizza_pdf", "preventivo", "ricevuta_pagamento", "carta_identita",
                "patente", "cf_codice_fiscale", "visura_camerale", "modulo_compilato",
                "comunicazione", "altro"];

export default function CRMDocumentsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [tipoF, setTipoF] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tipoF) params.set("tipo", tipoF);
      if (q.trim()) params.set("q", q.trim());
      const r = await axios.get(`${API}/api/admin/crm/documents?${params.toString()}`, { withCredentials: true });
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tipoF, q]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!confirm("Conferma soft delete?")) return;
    await axios.delete(`${API}/api/admin/crm/documents/${id}`, { withCredentials: true });
    load();
  };

  return (
    <div className="p-4 md:p-6" data-testid="crm-documents-page">
      <div className="flex flex-col md:flex-row justify-between gap-2 mb-4">
        <h2 className="text-xl font-semibold flex items-center gap-2"><FileText className="w-5 h-5" /> Documenti ({total})</h2>
        <button onClick={() => setShowUpload(true)} data-testid="crm-doc-upload-open-btn"
          className="px-3 py-2 text-sm bg-zinc-900 text-white rounded flex items-center gap-1">
          <Upload className="w-4 h-4" /> Upload
        </button>
      </div>
      <div className="flex gap-2 mb-3">
        <select value={tipoF} onChange={(e) => setTipoF(e.target.value)}
          className="px-2 py-2 text-sm rounded border border-zinc-200">
          <option value="">Tutti i tipi</option>
          {TIPI.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome file..."
          className="flex-1 px-3 py-2 text-sm rounded border border-zinc-200" />
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> :
        items.length === 0 ? <p className="text-zinc-400 italic text-center py-6">Nessun documento</p> :
        <div className="space-y-2">
          {items.map((d) => (
            <div key={d.id} className="border border-zinc-200 rounded p-3 flex items-center justify-between gap-3" data-testid={`crm-doc-${d.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{d.nome_file}</p>
                <p className="text-xs text-zinc-500">{d.tipo} · {(d.size_bytes / 1024).toFixed(1)} KB · {d.uploaded_at?.slice(0, 10)}</p>
              </div>
              <a href={`${API}/api/admin/crm/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                 className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`crm-doc-download-${d.id}`}>
                <Download className="w-4 h-4" />
              </a>
              <button onClick={() => del(d.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>}
      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUploaded={() => { setShowUpload(false); load(); }} />}
    </div>
  );
}

function UploadModal({ onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [tipo, setTipo] = useState("polizza_pdf");
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (custQuery.length < 2) { setCustResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/api/admin/crm/customers/search?q=${encodeURIComponent(custQuery)}`, { withCredentials: true })
        .then((r) => setCustResults(r.data.items || []));
    }, 250);
    return () => clearTimeout(t);
  }, [custQuery]);

  const submit = async () => {
    setError(null);
    if (!file || !selectedCust) { setError("File + cliente obbligatori"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", tipo);
      fd.append("customer_id", selectedCust.id);
      await axios.post(`${API}/api/admin/crm/documents/upload`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded();
    } catch (e) {
      setError(e.response?.data?.detail?.error_code || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="crm-doc-upload-modal">
        <h3 className="text-lg font-semibold mb-3">Upload documento</h3>
        <div className="space-y-2">
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded border border-zinc-200">
            {TIPI.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="relative">
            <input value={custQuery} onChange={(e) => { setCustQuery(e.target.value); setSelectedCust(null); }}
              placeholder="Cliente (ricerca nome/email)" className="w-full px-3 py-2 text-sm rounded border border-zinc-200" />
            {custResults.length > 0 && !selectedCust && (
              <div className="absolute z-10 bg-white border border-zinc-200 rounded w-full max-h-40 overflow-y-auto">
                {custResults.map((c) => (
                  <button key={c.id} onClick={() => { setSelectedCust(c); setCustQuery(c.anagrafica?.email || c.id); setCustResults([]); }}
                    className="block w-full text-left px-2 py-1 text-xs hover:bg-zinc-50">
                    {c.anagrafica?.ragione_sociale || `${c.anagrafica?.nome || ""} ${c.anagrafica?.cognome || ""}`}
                    <span className="text-zinc-400 ml-2">{c.anagrafica?.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedCust && <p className="text-xs text-emerald-700">✓ collegato</p>}
          <input type="file" onChange={(e) => setFile(e.target.files?.[0])} className="text-xs w-full" />
        </div>
        {error && <p className="text-xs text-rose-600 mt-2">{String(error)}</p>}
        <div className="flex gap-2 mt-3">
          <button onClick={onClose} className="flex-1 py-2 text-sm rounded bg-zinc-100">Annulla</button>
          <button onClick={submit} disabled={loading} className="flex-1 py-2 text-sm rounded bg-zinc-900 text-white disabled:opacity-50">
            {loading ? "Upload..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}
